import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateOverrideRule } from '../../../api/plan-benefit-override-rules-api'
import type {
  ListPlanBenefitOverrideRulesResponseDto,
  PlanBenefitOverrideRuleResponseDto,
  UpdatePlanBenefitOverrideRuleRequestDto,
} from '../../../api/dto/plan-benefit-override-rules.dto'
import { OVERRIDE_RULES_QUERY_KEY } from '../pages/ListPlanBenefitOverride/hooks/useGetOverrideRules'
import { OVERRIDE_RULE_QUERY_KEY } from './useGetOverrideRule'

const isStatsKey = (key: readonly unknown[]) =>
  key.length >= 2 && key[1] === 'stats'

interface MutationContext {
  lists: Array<
    [readonly unknown[], ListPlanBenefitOverrideRulesResponseDto | undefined]
  >
  rule: PlanBenefitOverrideRuleResponseDto | undefined
}

export function useUpdateOverrideRule(id: string) {
  const queryClient = useQueryClient()

  return useMutation<
    PlanBenefitOverrideRuleResponseDto,
    Error,
    UpdatePlanBenefitOverrideRuleRequestDto,
    MutationContext
  >({
    mutationFn: (body) => updateOverrideRule(id, body),

    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: [OVERRIDE_RULES_QUERY_KEY] })

      const lists =
        queryClient.getQueriesData<ListPlanBenefitOverrideRulesResponseDto>({
          queryKey: [OVERRIDE_RULES_QUERY_KEY],
          predicate: (q) => !isStatsKey(q.queryKey),
        })
      const rule = queryClient.getQueryData<PlanBenefitOverrideRuleResponseDto>(
        [OVERRIDE_RULE_QUERY_KEY, id]
      )

      const ctx: MutationContext = { lists, rule }

      // Patch each matching row in any cached list.
      for (const [queryKey, data] of lists) {
        if (!data?.data) continue
        const rowIndex = data.data.findIndex((r) => r.id === id)
        if (rowIndex < 0) continue

        queryClient.setQueryData(queryKey, {
          ...data,
          data: data.data.map((r) =>
            r.id === id ? { ...r, ...patchableFields(body, r) } : r
          ),
        })
      }

      if (rule) {
        queryClient.setQueryData<PlanBenefitOverrideRuleResponseDto>(
          [OVERRIDE_RULE_QUERY_KEY, id],
          { ...rule, ...patchableFields(body, rule) }
        )
      }

      return ctx
    },

    onError: (_err, _body, ctx) => {
      if (!ctx) return
      for (const [queryKey, data] of ctx.lists) {
        queryClient.setQueryData(queryKey, data)
      }
      if (ctx.rule !== undefined) {
        queryClient.setQueryData([OVERRIDE_RULE_QUERY_KEY, id], ctx.rule)
      }
    },

    onSuccess: (updated) => {
      queryClient.setQueryData([OVERRIDE_RULE_QUERY_KEY, id], updated)
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [OVERRIDE_RULES_QUERY_KEY] })
    },
  })
}

function patchableFields(
  body: UpdatePlanBenefitOverrideRuleRequestDto,
  current: PlanBenefitOverrideRuleResponseDto
): Partial<PlanBenefitOverrideRuleResponseDto> {
  const patch: Partial<PlanBenefitOverrideRuleResponseDto> = {}
  if (body.params !== undefined) patch.params = body.params
  if (body.sourceUrl !== undefined) patch.sourceUrl = body.sourceUrl
  if (body.notes !== undefined) patch.notes = body.notes
  patch.updatedAt = new Date().toISOString()
  patch.updatedBy = current.updatedBy
  return patch
}
